/**
 * API Expose Workflow Runner — Chat-style interface for consumer management.
 * Replaces the form-based ApiExposeAgentWorkflowsTab with a more user-friendly runner.
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Send as SendIcon,
  PersonAdd as PersonAddIcon,
  VpnKey as KeyIcon,
  Autorenew as RotateIcon,
  Security as ScopesIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { getDefaultTenantId } from '@/lib/cpClient';
import { CP_BASE } from '@/lib/http';
import { useAuthStore } from '@/store/authStore';
import {
  EXPOSE_TENANT_REQUIRED,
  buildExposeExecutePayload,
  presentExposeTenantCaption,
  resolveExposeExecuteTenant,
} from './exposeTenantHonesty';

interface WorkflowResult {
  workflow: string;
  success: boolean;
  data: any;
  error?: string;
}

const QUICK_ACTIONS = [
  { id: 'create_consumer', label: 'Create Consumer', icon: PersonAddIcon, prompt: 'Create a new API consumer', color: '#2563eb' },
  { id: 'mint_app_token', label: 'Mint Token', icon: KeyIcon, prompt: 'Mint a JWT token', color: '#7c3aed' },
  { id: 'rotate_consumer_key', label: 'Rotate Key', icon: RotateIcon, prompt: 'Rotate consumer key', color: '#059669' },
  { id: 'set_scopes', label: 'Set Scopes', icon: ScopesIcon, prompt: 'Configure scopes', color: '#d97706' },
];

export default function ApiExposeWorkflowRunner() {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null);
  const [results, setResults] = useState<WorkflowResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const selectedTenant = useAuthStore((s) => s.selectedTenant);
  const tenant = resolveExposeExecuteTenant({
    selectedTenant,
    hostTenant: getDefaultTenantId(),
  });

  // Build input fields for the selected workflow
  const [consumerName, setConsumerName] = useState('');
  const [consumerId, setConsumerId] = useState('');
  const [apiKey, setApiKey] = useState('');

  function getAuthHeaders() {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const s = useAuthStore.getState();
      if (s?.token) headers['Authorization'] = `Bearer ${s.token}`;
      if (tenant) headers['x-tenant-id'] = tenant;
    } catch { /* ignore */ }
    return headers;
  }

  async function executeWorkflow(workflow: string, workflowInput: any) {
    if (!tenant) {
      setError(EXPOSE_TENANT_REQUIRED);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${CP_BASE}/api-expose/agent/execute`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(buildExposeExecutePayload(workflow, workflowInput, tenant)),
      });
      const data = await resp.json();
      const result: WorkflowResult = {
        workflow,
        success: data.success !== false && resp.ok,
        data: data.result || data.internal_data || data,
        error: data.error || (!resp.ok ? `HTTP ${resp.status}` : undefined),
      };
      setResults(prev => [result, ...prev]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      setSelectedWorkflow(null);
    }
  }

  function handleQuickAction(action: typeof QUICK_ACTIONS[number]) {
    setSelectedWorkflow(action.id);
    setInput(action.prompt);
  }

  function handleExecute() {
    if (!selectedWorkflow) return;
    switch (selectedWorkflow) {
      case 'create_consumer':
        executeWorkflow('create_consumer', {
          consumer_name: consumerName || input,
          allowed_routes: ['/public/*'],
        });
        break;
      case 'mint_app_token':
        executeWorkflow('mint_app_token', { api_key: apiKey });
        break;
      case 'rotate_consumer_key':
        executeWorkflow('rotate_consumer_key', { consumer_id: consumerId });
        break;
      case 'set_scopes':
        executeWorkflow('set_scopes', { consumer_id: consumerId, patch: {} });
        break;
      default:
        executeWorkflow(selectedWorkflow, { input });
    }
  }

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {presentExposeTenantCaption(tenant)}
      </Typography>

      {/* Quick Actions */}
      {!selectedWorkflow && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          {QUICK_ACTIONS.map(action => (
            <Button
              key={action.id}
              variant="outlined"
              startIcon={<action.icon />}
              onClick={() => handleQuickAction(action)}
              sx={{ borderColor: action.color, color: action.color, textTransform: 'none', '&:hover': { borderColor: action.color, backgroundColor: `${action.color}10` } }}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}

      {/* Workflow Form */}
      {selectedWorkflow && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2">
              {QUICK_ACTIONS.find(a => a.id === selectedWorkflow)?.label || selectedWorkflow}
            </Typography>
            <Chip label={selectedWorkflow} size="small" variant="outlined" />
          </Box>

          {selectedWorkflow === 'create_consumer' && (
            <TextField
              fullWidth size="small" label="Consumer Name" placeholder="e.g., Trekker App"
              value={consumerName} onChange={e => setConsumerName(e.target.value)}
              sx={{ mb: 1.5 }}
            />
          )}
          {(selectedWorkflow === 'rotate_consumer_key' || selectedWorkflow === 'set_scopes') && (
            <TextField
              fullWidth size="small" label="Consumer ID" placeholder="consumer_xxx"
              value={consumerId} onChange={e => setConsumerId(e.target.value)}
              sx={{ mb: 1.5 }}
            />
          )}
          {selectedWorkflow === 'mint_app_token' && (
            <TextField
              fullWidth size="small" label="API Key" placeholder="ak_xxx..."
              value={apiKey} onChange={e => setApiKey(e.target.value)}
              sx={{ mb: 1.5 }}
            />
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained" size="small"
              startIcon={loading ? <CircularProgress size={14} /> : <SendIcon />}
              onClick={handleExecute} disabled={loading || !tenant}
            >
              {loading ? 'Executing...' : 'Execute'}
            </Button>
            <Button variant="text" size="small" onClick={() => setSelectedWorkflow(null)}>Cancel</Button>
          </Box>
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {copied && <Alert severity="success" sx={{ mb: 1, py: 0 }}>Copied to clipboard</Alert>}

      {/* Results */}
      {results.map((r, i) => (
        <Card key={i} variant="outlined" sx={{ mb: 1.5, borderColor: r.success ? 'success.light' : 'error.light' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={r.workflow} size="small" color={r.success ? 'success' : 'error'} variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  {r.success ? 'Success' : `Failed: ${r.error}`}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => copyText(JSON.stringify(r.data, null, 2))}>
                <CopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
            <Box sx={{ fontFamily: 'monospace', fontSize: 11, maxHeight: 120, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
              <pre style={{ margin: 0 }}>{JSON.stringify(r.data, null, 2)}</pre>
            </Box>
          </CardContent>
        </Card>
      ))}

      {results.length === 0 && !selectedWorkflow && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Select a quick action above to manage API consumers, tokens, and scopes.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
